import { useLayoutEffect, useMemo, useEffect, useRef, useState } from 'react';
import type { RelationPickerTarget } from '../grid/components/dialogs/RelationPickerDialog';
import {
  useGetSpecificationQuery,
  useSaveSpecificationMutation,
  useSaveEntityMutation,
} from '../grid/api/gridApi';
import type { EntityField, SaveEntityRequest } from '../grid';

type UseSpecificationFormParams = {
  blockKey: string;
  parentId: string;
  mode: 'add' | 'edit';
  contentKey?: string;
  onRegisterRelationTarget?: (target: RelationPickerTarget) => () => void;
};

export const useSpecificationForm = ({
  blockKey,
  parentId,
  mode,
  contentKey,
  onRegisterRelationTarget,
}: UseSpecificationFormParams) => {
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const saveSuccessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const timer = saveSuccessTimerRef.current;
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  const skip = mode === 'add' || !parentId;

  const { data, isLoading, isError, refetch } = useGetSpecificationQuery(Number(parentId), {
    skip,
  });

  // Used only for updating the set of linked specs (add/remove links via the relation picker).
  // It does NOT persist SpecificationValue for an already-linked spec — see handleSave below,
  // which goes through Grid/Save instead, same as the legacy form.
  const [saveSpecification] = useSaveSpecificationMutation();

  // Used for persisting actual field values — mirrors what the legacy detail-form Save button
  // does (Grid/Save with isSpec fields), because AssetSpecification/SaveSpecifications only
  // upserts the asset<->spec link and ignores value changes on an existing link.
  const [saveEntity, { isLoading: isSavingValues, error: saveValuesError }] =
    useSaveEntityMutation();

  useEffect(() => {
    if (!data || data.Code !== 1 || !Array.isArray(data.Data)) return;
    const initial: Record<string, string> = {};
    for (const f of data.Data) {
      initial[f.key] = f.value || f.defaultValue || '';
    }
    setFieldValues(initial);
  }, [data]);

  // Build targetRows in the format RelationPickerDialog expects:
  // each row must have [targetGridField] = specControlId so existingIds is populated correctly,
  // which causes already-linked specs to render as disabled (checked) in the picker.
  const existingTargetRows = useMemo(() => {
    if (!data?.Data) return [];
    return data.Data.filter((f) => f.additionalData != null).map((f) => ({
      CrmRelations: String(f.additionalData!.specControlId),
    }));
  }, [data]);

  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Register as a RelationPickerTarget so the parent form's "addrelations" toolbar
  // command can open the spec picker and write confirmed rows back here.
  useLayoutEffect(() => {
    if (!onRegisterRelationTarget || !blockKey || mode === 'add' || !parentId) return undefined;

    return onRegisterRelationTarget({
      gridKey: blockKey,
      targetRows: existingTargetRows,
      selectedRows: undefined,
      hasUnsavedChanges: false,
      allowDeselect: true,
      onAddRowWithData: async (fieldData: Record<string, unknown> | Record<string, unknown>[]) => {
        const rows = Array.isArray(fieldData) ? fieldData : [fieldData];
        if (!rows.length) return;

        const addedIds = new Set(
          rows
            .filter((r) => r['_action'] !== 'remove')
            .map((r) => Number(r['CrmRelations']))
            .filter(Boolean)
        );
        const removedIds = new Set(
          rows
            .filter((r) => r['_action'] === 'remove')
            .map((r) => Number(r['CrmRelations']))
            .filter(Boolean)
        );

        // Merge current existing with delta: keep existing minus removed, add new ones
        const existingSpecIds = new Set(
          (dataRef.current?.Data ?? [])
            .filter((f) => f.additionalData != null)
            .map((f) => f.additionalData!.specControlId)
        );

        // Preserve current values for specs that stay linked; only genuinely new specs get ''
        const valueBySpecId = new Map(
          (dataRef.current?.Data ?? [])
            .filter((f) => f.additionalData != null)
            .map((f) => [f.additionalData!.specControlId, f.value || f.defaultValue || ''])
        );

        const finalIds = new Set([
          ...Array.from(existingSpecIds).filter((id) => !removedIds.has(id)),
          ...addedIds,
        ]);

        const values = Array.from(finalIds).map((specId) => ({
          AssetsId: Number(parentId),
          SpecificationId: specId,
          SpecificationValue: valueBySpecId.get(specId) ?? '',
        }));

        await saveSpecification({
          assetId: Number(parentId),
          key: contentKey ?? blockKey,
          values,
        }).unwrap();

        refetch();
      },
    });
  }, [
    onRegisterRelationTarget,
    contentKey,
    blockKey,
    mode,
    parentId,
    existingTargetRows,
    saveSpecification,
    refetch,
  ]);

  const setFieldValue = (key: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!data || data.Code !== 1 || !Array.isArray(data.Data)) return;
    setSaveSuccess(false);

    try {
      // Values must go through the same Grid/Save entity endpoint the legacy form uses —
      // AssetSpecification/SaveSpecifications only upserts the link, it does not update
      // SpecificationValue for an existing link.
      const fields: EntityField[] = data.Data.filter((f) => f.additionalData != null).map((f) => ({
        FieldName: f.dataField,
        StringValue: fieldValues[f.key] ?? '',
        IsSpec: true,
      }));

      const saveRequest: SaveEntityRequest = {
        Id: parentId,
        NodeKey: contentKey ?? blockKey,
        Fields: fields,
      };

      const result = await saveEntity(saveRequest).unwrap();
      if (result.data?.Code !== 1) {
        throw new Error(result.data?.Error || result.message || 'Failed to save specifications.');
      }

      setSaveSuccess(true);
      if (saveSuccessTimerRef.current) clearTimeout(saveSuccessTimerRef.current);
      saveSuccessTimerRef.current = setTimeout(() => setSaveSuccess(false), 3000);
      refetch();
    } catch {
      // error shown via saveValuesError
    }
  };

  return {
    data,
    isLoading,
    isError,
    fieldValues,
    setFieldValue,
    handleSave,
    isSavingValues,
    saveValuesError,
    saveSuccess,
  };
};
